import { Content, Link } from '@backstage/core-components';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { configApiRef, IconComponent, useApi, useApp } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Box,
    makeStyles,
    CardHeader,
    CardActions,
    Button,
    ImageList,
    ImageListItem,
} from '@material-ui/core';
import { projectPageTranslationRef } from '../../translation/project-page/project-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { applicationPageTranslationRef } from '../../translation/application-page/application-page';
import LanguageIcon from '@material-ui/icons/Language';
import { ApplicationForm } from './ApplicationForm';
import { useOpenFgaPermission } from '../../../hooks/useOpenFgaPermission';

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        transition: '0.2s',
        display: 'flex',
        flexDirection: 'column',

        '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
        },
    },

    header: {
        color: theme.getPageTheme({ themeId: 'default' }).fontColor,
        padding: theme.spacing(2, 2, 3),
        backgroundImage: theme.getPageTheme({ themeId: 'default' }).backgroundImage,
        backgroundPosition: 0,
        backgroundSize: 'inherit',
    },

    content: {
        flexGrow: 1,
    },

    actions: {
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },

    description: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        minHeight: 50,
    },

    svgIcon: {
        display: 'inline-block',
        '& svg': {
            display: 'inline-block',
            fontSize: 'inherit',
            verticalAlign: 'baseline',
        },
    },
}));

export const ApplicationRepositories = () => {
    const classes = useStyles();
    const { entity } = useEntity();
    const catalogApi = useApi(catalogApiRef);
    const config = useApi(configApiRef);
    const SYS_INFO_BACKEND_URL = 'app.baseUrl';
    const frontendUrl = config.getString(SYS_INFO_BACKEND_URL);

    const tApp = useTranslationRef(applicationPageTranslationRef);
    const tProj = useTranslationRef(projectPageTranslationRef);

    const { allowed: applicationTabAllowed } = useOpenFgaPermission('can_view_details', 'application');

    const appRef = `application:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;

    const { allowed: canRequestRepo } = useOpenFgaPermission('can_manage_config', 'application');

    const { loading, error, value: repositories } = useAsync(async () => {
        const response = await catalogApi.getEntities({ filter: { kind: 'Component', 'spec.system': appRef } });

        return response.items.filter(item => item.spec?.type !== 'request');
    }, [appRef, catalogApi]);

    if (loading) {
        return (
            <Content>
                <Box display="flex" justifyContent="center"><CircularProgress /></Box>
            </Content>
        );
    }

    if (error) {
        return (
            <Content>
                <Typography color="error">Error al cargar aplicaciones</Typography>
            </Content>
        );
    }

    const app = useApp();

    const iconResolver = (key?: string): IconComponent =>
        key ? app.getSystemIcon(key) ?? LanguageIcon : LanguageIcon;

    const handleClickButton = async () => {
        let divisionId: string | undefined = undefined;

        // ✅ SOLO si existe division
        if (entity.spec?.division) {
            try {
                const company: any = await catalogApi.getEntityByRef(
                    entity.spec.division as string,
                );

                if (company?.spec?.subDivisionOf) {
                    const division: any = await catalogApi.getEntityByRef(
                        company.spec.subDivisionOf,
                    );

                    divisionId = division?.spec?.company_id;
                }
            } catch (err) {
                console.warn('⚠️ Error resolving division from button:', err);
            }
        }

        // ✅ Construcción dinámica del objeto
        const applicationProject: any = {
            application: entity.spec?.number,
            applicationRef: `application:${entity.metadata.namespace}/${entity.metadata.name}`,
            applicationOwnerRef: `group:${entity.metadata.namespace}/equipo-${entity.metadata.name}`,
            project: '',
            projectRef: '',
        };

        // ✅ SOLO añadir si existe
        if (divisionId) {
            applicationProject.applicationDivision = divisionId;
        }

        const url = `${frontendUrl}/create/templates/default/component-request?formData=${encodeURIComponent(
            JSON.stringify({
                applicationProject,
            }),
        )}`;

        window.open(url, '_self');
    };

    return (
        <>
            <Grid item xs={12} style={{ marginBottom: 30 }}>
                <ApplicationForm />
            </Grid>
            {applicationTabAllowed && (
                <>
                    <Grid container direction='row' alignItems='center' justifyContent='space-between'>
                        <Grid item>
                            <Typography variant="h5" gutterBottom>
                                {tApp.t('applicationRepositories.title')}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {canRequestRepo && (
                                <Button variant="contained" color="primary" onClick={handleClickButton}>
                                    {tApp.t('applicationRepositories.requestRepo')}
                                </Button>
                            )}
                        </Grid>
                    </Grid>

                    {(repositories && repositories?.length > 0) ? (
                        <Grid container spacing={3}>
                            {repositories?.map((repo: any) => (
                                <Grid item xs={12} sm={6} md={4} xl={3} key={repo.metadata.uid}>
                                    <Card className={classes.card}>
                                        <CardHeader
                                            className={classes.header}
                                            title={
                                                <Box>
                                                    {repo.spec?.type && (
                                                        <Typography variant="subtitle2" component="h3">
                                                            {repo.spec?.type}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="h6" component="h4">
                                                        {repo.metadata.title ?? repo.metadata.name}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <CardContent>

                                            {/* Links */}

                                            {repo.metadata.links && (
                                                <>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {`Links`}
                                                    </Typography>
                                                    <ImageList rowHeight="auto">
                                                        {repo.metadata.links.map((link: any) => (
                                                            <ImageListItem key={link.url}>
                                                                <Box display="flex">
                                                                    <Box mr={1} className={classes.svgIcon}>
                                                                        <Typography component="div">
                                                                            {link.icon ? (() => {
                                                                                const Icon = iconResolver(link.icon);
                                                                                return <Icon />;
                                                                            })() : <LanguageIcon />}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box flexGrow="1">
                                                                        <Link to={link.url} target="_blank" rel="noopener">
                                                                            {link.title || link.url}
                                                                        </Link>
                                                                    </Box>
                                                                </Box>
                                                            </ImageListItem>
                                                        ))}
                                                    </ImageList>
                                                </>
                                            )}

                                            {/* Ciclo de Vida */}
                                            <Typography variant="body2" color="textSecondary">
                                                {tProj.t('projectTableColumns.lifecycle')}
                                            </Typography>
                                            <Typography variant="body1">
                                                {repo.spec?.lifecycle ?? '—'}
                                            </Typography>

                                        </CardContent>

                                        <CardActions className={classes.actions}>
                                            <Button
                                                size="small"
                                                color="primary"
                                                component="a"
                                                href={`/catalog/${repo.metadata.namespace ?? 'default'}/${repo.kind.toLowerCase()}/${repo.metadata.name}`}
                                            >
                                                {tApp.t('applicationRepositories.moreDetails')}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>) :
                        <Typography>{tApp.t('applicationRepositories.emptyRepositories')}</Typography>}
                </>
            )}
        </>
    );
};
